import React from 'react';
import { cn } from '@/lib/utils';
import { ArrowLeft, ArrowRight, Send } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';

type Step = {
  key: string;
  label: string;
  content: React.ReactNode;
};

type TraceabilityFormStepsProps = {
  steps: Step[];
  currentStep: number;
  progress: number;
  sectionRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  isLastStep: boolean;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onSubmitForm: (e: React.FormEvent) => Promise<void>;
  onNext: () => void;
  onPrevious: () => void;
  onSubmit: (e: React.FormEvent | null) => Promise<void>;
};

export function TraceabilityFormSteps({
  steps,
  currentStep,
  progress,
  sectionRefs,
  isLastStep,
  onKeyDown,
  onSubmitForm,
  onNext,
  onPrevious,
  onSubmit,
}: TraceabilityFormStepsProps) {
  const { t } = useTranslation();

  return (
    <form
      onKeyDown={onKeyDown}
      onSubmit={onSubmitForm}
      className="flex flex-col gap-4"
    >
      <div className="space-y-4">
        {steps.map((step, index) =>
          index === currentStep ? (
            <div
              key={step.key}
              ref={(el) => {
                sectionRefs.current[index] = el;
              }}
              className="rounded-2xl border bg-card/80 p-3 shadow-sm backdrop-blur-sm"
            >
              <div className="mb-3 flex items-center gap-3">
                <span className="text-xs font-semibold tabular-nums text-muted-foreground">
                  {currentStep + 1}/{steps.length}
                </span>
                <div
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
                >
                  <div
                    className={cn('h-full rounded-full bg-primary transition-all duration-300 ease-in-out')}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs font-semibold text-primary">
                  {steps[currentStep].label}
                </span>
              </div>
              {step.content}
            </div>
          ) : null
        )}
      </div>

      <div className="flex flex-wrap justify-between gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="flex-1 gap-2 sm:w-auto sm:flex-none"
          disabled={currentStep === 0}
          onClick={onPrevious}
        >
          <ArrowLeft className="h-4 w-4" /> {t('traceability.form.previous')}
        </Button>

        {isLastStep ? (
          <Button
            type="submit"
            variant="default"
            size="lg"
            className="flex-1 gap-2 sm:w-auto sm:flex-none"
            onClick={onSubmit}
          >
            {t('traceability.form.submit')} <Send className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="default"
            size="lg"
            className="flex-1 gap-2 sm:w-auto sm:flex-none"
            onClick={onNext}
          >
            {t('traceability.form.next')} <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </form>
  );
}
